import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import ReferralCard from "../components/ReferralCard";
import { WalletIcon, TransactionIcon, UserIcon } from "../components/Icons";
import LiveExchangePrices from "../components/LiveExchangePrices";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { goldAPI, userAPI, walletAPI } from "../services/api";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    walletBalance: 0,
    mainBalance: 0,
    profitBalance: 0,
    rewardBalance: 0,
    investAmount: 0,
    workingAmount: 0,
    nonWorkingAmount: 0,
    todayProfit: 0,
    profitPercentageChange: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [joinDateLabel, setJoinDateLabel] = useState("");
  const [joinTimeLabel, setJoinTimeLabel] = useState("");

  const isCreditTransaction = (txType, amount) => {
    const normalizedType = String(txType || "").toUpperCase();
    if (
      normalizedType.includes("WITHDRAW") ||
      normalizedType.includes("DEBIT") ||
      normalizedType.includes("FEE")
    ) {
      return false;
    }
    if (
      normalizedType.includes("DEPOSIT") ||
      normalizedType.includes("CREDIT") ||
      normalizedType.includes("BONUS") ||
      normalizedType.includes("REWARD") ||
      normalizedType.includes("COMMISSION")
    ) {
      return true;
    }
    return Number(amount || 0) >= 0;
  };

  const formatTransactionType = (rawType) => {
    const type = String(rawType || "").toLowerCase();
    const customLabels = {
      bot_license_fee: "Bot License Fee",
      affiliate_commission: "Affiliate Commission",
      arbitrage_trade_close: "Arbitrage Trade Settlement",
      arbitrage_profit: "Arbitrage Profit",
      crypto_deposit: "Crypto Deposit",
      crypto_withdrawal: "Crypto Withdrawal",
      credit: "Credit",
      leadership_reward: "Leadership Reward",
      performance_award: "Performance Award",
    };
    if (customLabels[type]) return customLabels[type];
    return String(rawType || "Transaction")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  /** Recent-transactions whitelist (PDF): hide generic `debit` trade-lock rows; keep settlement, bot fee, deposits, withdrawals, credit. */
  const isRecentTransactionWhitelisted = (rawType) => {
    const t = String(rawType || "").toLowerCase();
    if (t === "debit") return false;
    const allowed = new Set([
      "credit",
      "crypto_deposit",
      "crypto_withdrawal",
      "bot_license_fee",
      "arbitrage_trade_close",
    ]);
    return allowed.has(t);
  };

  // Fetch all dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [walletRes, metricsRes, transactionsRes, profileRes, incomeSummaryRes, goldPnLRes] =
        await Promise.allSettled([
          walletAPI.getWallet(),
          userAPI.getDashboardMetrics(),
          walletAPI.getTransactions("limit=200"),
          userAPI.getProfile(),
          userAPI.getIncomeSummary(),
          goldAPI.getPnL(),
        ]);

      const walletBalance =
        walletRes.status === "fulfilled"
          ? Number(walletRes.value?.wallet?.balance || 0)
          : 0;

      const metrics =
        metricsRes.status === "fulfilled" ? metricsRes.value || {} : {};

      if (profileRes.status === "fulfilled" && profileRes.value?.createdAt) {
        const created = new Date(profileRes.value.createdAt);
        if (!Number.isNaN(created.getTime())) {
          setJoinDateLabel(
            created.toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
          );
          setJoinTimeLabel(
            created.toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            }),
          );
          try {
            const raw = localStorage.getItem("user");
            const prev = raw ? JSON.parse(raw) : {};
            localStorage.setItem(
              "user",
              JSON.stringify({
                ...prev,
                ...profileRes.value,
                createdAt: profileRes.value.createdAt,
              }),
            );
          } catch {
            /* ignore */
          }
        }
      }

      let walletTransactions = [];
      if (transactionsRes.status === "fulfilled") {
        walletTransactions = transactionsRes.value?.transactions || [];
        const processedTransactions = walletTransactions
          .filter((tx) => Math.abs(parseFloat(tx.amount || 0)) > 0)
          .filter((tx) => isRecentTransactionWhitelisted(tx.type))
          .slice(0, 6)
          .map((tx) => {
            const rawAmount = parseFloat(tx.amount || 0);
            const amount = Math.abs(rawAmount);
            const txStatus = String(tx.status || "completed").toLowerCase();
            const isCredit = isCreditTransaction(tx.type, rawAmount);

            return {
              type: formatTransactionType(tx.type),
              amount: `${isCredit ? "+" : "-"}${amount.toFixed(2)} USDT`,
              status:
                txStatus === "completed" || txStatus === "success"
                  ? "Completed"
                  : txStatus === "pending" || txStatus === "processing"
                    ? "Pending"
                    : "Failed",
              date: new Date(tx.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              }),
              statusColor:
                txStatus === "completed" || txStatus === "success"
                  ? "gold"
                  : txStatus === "pending" || txStatus === "processing"
                    ? "yellow"
                    : "red",
            };
          });
        setTransactions(processedTransactions);
      } else {
        setTransactions([]);
      }

      const incomeSummaryData =
        incomeSummaryRes.status === "fulfilled" ? incomeSummaryRes.value : null;
      const recentEntries = incomeSummaryData?.recentEntries || [];
      const goldPnLRecords =
        goldPnLRes.status === "fulfilled"
          ? goldPnLRes.value?.data?.records || []
          : [];
      const now = new Date();
      /** UTC calendar day so “today” matches server timestamps (avoids local/UTC drift). */
      const dayStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0),
      );
      const yesterdayStart = new Date(dayStart);
      yesterdayStart.setUTCDate(yesterdayStart.getUTCDate() - 1);
      const yesterdayEnd = new Date(dayStart.getTime() - 1);

      const todayIncomeProfit = recentEntries.reduce((sum, entry) => {
        const createdAt = new Date(entry.createdAt);
        if (Number.isNaN(createdAt.getTime()) || createdAt < dayStart) return sum;
        return sum + Number(entry.netAmount || 0);
      }, 0);
      const yesterdayIncomeProfit = recentEntries.reduce((sum, entry) => {
        const createdAt = new Date(entry.createdAt);
        if (
          Number.isNaN(createdAt.getTime()) ||
          createdAt < yesterdayStart ||
          createdAt > yesterdayEnd
        )
          return sum;
        return sum + Number(entry.netAmount || 0);
      }, 0);

      const todayGoldProfit = goldPnLRecords.reduce((sum, record) => {
        const realizedAt = new Date(record.realizationDate);
        if (Number.isNaN(realizedAt.getTime()) || realizedAt < dayStart) return sum;
        return sum + Number(record.netPnL || 0);
      }, 0);
      const yesterdayGoldProfit = goldPnLRecords.reduce((sum, record) => {
        const realizedAt = new Date(record.realizationDate);
        if (
          Number.isNaN(realizedAt.getTime()) ||
          realizedAt < yesterdayStart ||
          realizedAt > yesterdayEnd
        )
          return sum;
        return sum + Number(record.netPnL || 0);
      }, 0);

      /**
       * Do not add arbitrage trade history here: capped arb profit is already in income ledger
       * (applyIncomeWithCap). Summing trades too doubled “Today's Profit” vs profit wallet.
       */
      const todayProfit = todayIncomeProfit + todayGoldProfit;
      const yesterdayProfit = yesterdayIncomeProfit + yesterdayGoldProfit;

      const profitPercentageChange =
        yesterdayProfit > 0
          ? ((todayProfit - yesterdayProfit) / yesterdayProfit) * 100
          : todayProfit > 0
            ? 100
            : 0;

      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthlyProfitMap = new Map();
      recentEntries.forEach((entry) => {
        const date = new Date(entry.createdAt);
        if (Number.isNaN(date.getTime())) return;
        const key = `${date.getFullYear()}-${date.getMonth()}`;
        monthlyProfitMap.set(
          key,
          (monthlyProfitMap.get(key) || 0) + Number(entry.netAmount || 0),
        );
      });
      goldPnLRecords.forEach((record) => {
        const date = new Date(record.realizationDate);
        if (Number.isNaN(date.getTime())) return;
        const key = `${date.getFullYear()}-${date.getMonth()}`;
        monthlyProfitMap.set(
          key,
          (monthlyProfitMap.get(key) || 0) + Number(record.netPnL || 0),
        );
      });

      const monthlyExpensesMap = new Map();
      walletTransactions.forEach((tx) => {
        const date = new Date(tx.createdAt);
        if (Number.isNaN(date.getTime())) return;
        const type = String(tx.type || "").toUpperCase();
        const isExpense = type.includes("WITHDRAW") || type.includes("DEBIT");
        if (!isExpense) return;
        const key = `${date.getFullYear()}-${date.getMonth()}`;
        monthlyExpensesMap.set(
          key,
          (monthlyExpensesMap.get(key) || 0) + Math.abs(Number(tx.amount || 0)),
        );
      });

      const currentYear = now.getFullYear();
      const generatedChartData = months.map((month, monthIndex) => {
        const key = `${currentYear}-${monthIndex}`;
        return {
          month,
          profit: Number((monthlyProfitMap.get(key) || 0).toFixed(2)),
          expenses: Number((monthlyExpensesMap.get(key) || 0).toFixed(2)),
        };
      });

      setDashboardData({
        walletBalance,
        mainBalance: Number(metrics.mainBalance ?? walletBalance),
        profitBalance: Number(metrics.profitBalance ?? 0),
        rewardBalance: Number(metrics.rewardBalance ?? 0),
        investAmount: Number(metrics.investAmount ?? 0),
        workingAmount: Number(metrics.workingAmount ?? 0),
        nonWorkingAmount: Number(metrics.nonWorkingAmount ?? 0),
        todayProfit: Number(todayProfit.toFixed(2)),
        profitPercentageChange: Number(profitPercentageChange.toFixed(2)),
      });
      setChartData(generatedChartData);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (color) => {
    switch (color) {
      case "gold":
        return "bg-gradient-to-r from-[#22c55e]/20 to-[#16a34a]/20 text-primary border border-primary/50";
      case "yellow":
        return "bg-yellow-500/20 text-yellow-400 border border-yellow-400/30";
      case "red":
        return "bg-red-500/20 text-red-400 border border-red-400/30";
      default:
        return "bg-gray-500/20 text-[var(--text-secondary)] border border-gray-400/30";
    }
  };

  // Quick Actions
  const quickActions = [
    {
      label: "Make a Deposit",
      icon: WalletIcon,
      action: () => navigate("/wallet"),
    },
    {
      label: "Initiate Withdrawal",
      icon: TransactionIcon,
      action: () => navigate("/withdrawal"),
    },
    {
      label: "Team",
      icon: UserIcon,
      action: () => navigate("/mlm-team"),
    },
  ];

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)] overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 min-w-0 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-1 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Dashboard
            </h1>
            <p className="text-[var(--text-primary)] text-sm md:text-base mb-1">
              Your wallet and today&apos;s performance at a glance.
            </p>
            {joinDateLabel ? (
              <p className="text-xs text-[var(--text-primary)] opacity-70">
                Joined {joinDateLabel}
                {joinTimeLabel ? ` · ${joinTimeLabel}` : ""}
              </p>
            ) : null}
          </div>
        </div>

        <div className="mb-8">
          <ReferralCard />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="hover:transform hover:scale-105 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-[var(--text-primary)] opacity-70 mb-2">
                  Principal (Main)
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">
                  {loading ? "..." : dashboardData.mainBalance.toFixed(2)}
                </h3>
                <p className="text-xs text-[var(--text-primary)] opacity-60 mt-1">USDT</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#22c55e]/20 to-[#16a34a]/20">
                <WalletIcon className="w-6 h-6 text-[var(--text-primary)]" />
              </div>
            </div>
            <p className="text-xs text-[var(--text-primary)] opacity-60">Main wallet</p>
          </Card>

          <Card className="hover:transform hover:scale-105 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-[var(--text-primary)] opacity-70 mb-2">
                  Total Profit (Profit wallet)
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-green-400">
                  {loading ? "..." : dashboardData.profitBalance.toFixed(2)}
                </h3>
                <p className="text-xs text-[var(--text-primary)] opacity-60 mt-1">USDT</p>
              </div>
            </div>
          </Card>

          {/* Today's Profit */}
          <Card className="hover:transform hover:scale-105 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-[var(--text-primary)] opacity-70 mb-2">
                  Today's Profit
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-green-400">
                  {loading
                    ? "..."
                    : dashboardData.todayProfit >= 0
                      ? `+${dashboardData.todayProfit.toFixed(2)}`
                      : dashboardData.todayProfit.toFixed(2)}
                </h3>
                <p className="text-xs text-[var(--text-primary)] opacity-60 mt-1">USDT</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/20 to-green-600/20">
                <svg
                  className="w-6 h-6 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            {/* <p className="text-xs text-[var(--text-primary)] opacity-50 mt-1">
              Income ledger + gold (UTC day). Arbitrage is counted once via ledger, not trade list.
            </p> */}
            <p
              className={`text-xs mt-1 ${
                dashboardData.profitPercentageChange >= 0
                  ? "text-green-400"
                  : "text-red-400"
              }`}
            >
              {loading
                ? "..."
                : `${dashboardData.profitPercentageChange >= 0 ? "+" : ""}${dashboardData.profitPercentageChange.toFixed(1)}% ${dashboardData.profitPercentageChange >= 0 ? "increase" : "decrease"} from yesterday`}
            </p>
          </Card>

          <Card>
            <p className="text-sm text-[var(--text-primary)] opacity-70 mb-2">
              Invest Amount
            </p>
            <h3 className="text-2xl font-bold text-[var(--text-primary)]">
              {loading ? "..." : dashboardData.investAmount.toFixed(2)} USDT
            </h3>
            <p className="text-xs opacity-60 mt-1">Total deposited</p>
          </Card>

          <Card>
            <p className="text-sm text-[var(--text-primary)] opacity-70 mb-2">
              Working Amount
            </p>
            <h3 className="text-2xl font-bold text-[var(--text-primary)]">
              {loading ? "..." : dashboardData.workingAmount.toFixed(2)} USDT
            </h3>
            <p className="text-xs opacity-60 mt-1">Referral + trading income (capped)</p>
          </Card>

          <Card>
            <p className="text-sm text-[var(--text-primary)] opacity-70 mb-2">
              Non-Working Amount
            </p>
            <h3 className="text-2xl font-bold text-[var(--text-primary)]">
              {loading ? "..." : dashboardData.nonWorkingAmount.toFixed(2)} USDT
            </h3>
            <p className="text-xs opacity-60 mt-1">Bot trading profits</p>
          </Card>

          <Card>
            <p className="text-sm text-[var(--text-primary)] opacity-70 mb-2">
              Reward Wallet
            </p>
            <h3 className="text-2xl font-bold text-[var(--text-primary)]">
              {loading ? "..." : dashboardData.rewardBalance.toFixed(2)} USDT
            </h3>
            <p className="text-xs opacity-60 mt-1">Referral & rewards bucket</p>
          </Card>
        </div>

        {/* Live exchange spot prices */}
        <Card className="mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Live exchange prices
          </h2>
          <LiveExchangePrices />
        </Card>

        {/* Trading Performance & Recent Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Trading Performance Chart - 2 columns */}
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                Trading Performance
              </h2>
              <button
                onClick={() => navigate("/trading-performance")}
                className="text-sm text-[var(--text-primary)] hover:text-primary/80 transition-colors flex items-center gap-1"
              >
                View Details
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>

            <ResponsiveContainer
              width="100%"
              height={250}
              className="md:!h-[300px]"
            >
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient
                    id="profitGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient
                    id="expensesGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#6B7280" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#374151" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis
                  dataKey="month"
                  stroke="#A1A1AA"
                  style={{ fontSize: "12px" }}
                />
                <YAxis
                  stroke="#A1A1AA"
                  style={{ fontSize: "12px" }}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#161A1D",
                    border: "1px solid #22c55e",
                    borderRadius: "12px",
                    color: "#22c55e",
                  }}
                  formatter={(value) => [`$${value}`, ""]}
                />
                <Legend wrapperStyle={{ color: "#22c55e" }} iconType="circle" />
                <Area
                  type="monotone"
                  dataKey="profit"
                  stroke="#22c55e"
                  strokeWidth={2}
                  fill="url(#profitGradient)"
                  name="Profit"
                />
                <Area
                  type="monotone"
                  dataKey="expenses"
                  stroke="#6B7280"
                  strokeWidth={2}
                  fill="url(#expensesGradient)"
                  name="Expenses"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* Recent Transactions - 1 column */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                Recent Transactions
              </h2>
              <button
                onClick={() => navigate("/transactions")}
                className="text-sm text-[var(--text-primary)] hover:text-primary/80 transition-colors"
              >
                View All
              </button>
            </div>

            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="mt-2 text-sm text-[var(--text-primary)] opacity-60">
                    Loading transactions...
                  </p>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-[var(--text-primary)] opacity-60">
                    No transactions yet
                  </p>
                </div>
              ) : (
                transactions.map((tx, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-xl bg-surface-soft border border-primary/10 hover:border-primary/30 transition-all"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-[var(--text-primary)]">
                        {tx.type}
                      </span>
                      <span
                        className={`text-sm font-bold ${
                          tx.amount.startsWith("+")
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {tx.amount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span
                        className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${getStatusBadgeClass(
                          tx.statusColor,
                        )}`}
                      >
                        {tx.status}
                      </span>
                      <span className="text-xs text-[var(--text-primary)] opacity-60">
                        {tx.date}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Quick Actions
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {quickActions.map((action, index) => {
              const IconComponent = action.icon;
              return (
                <button
                  key={index}
                  onClick={action.action}
                  className="p-4 rounded-xl bg-[var(--bg-main)] border border-primary/20 hover:bg-[#22c55e]/10 hover:border-primary hover:shadow-lg hover:shadow-primary/20 transition-all group"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-[#22c55e]/20 to-[#16a34a]/20 group-hover:from-[#22c55e]/30 group-hover:to-[#16a34a]/30 transition-all">
                      <IconComponent className="w-6 h-6 text-[var(--text-primary)]" />
                    </div>
                    <span className="text-sm font-semibold text-[var(--text-primary)] text-center">
                      {action.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-sm text-[var(--text-primary)] opacity-60">
            © 2026 Aiarbix. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
